export type ConfigurationRole = 'owner' | 'admin' | 'manager' | 'operator';

export enum RecordAction {
	Add = 'add',
	Edit = 'edit',
	Delete = 'delete',
}

export interface ConfigurationCapabilities {
	readonly manage_store: boolean;
	readonly create_store: boolean;
	readonly manage_users: boolean;
	readonly manage_domains: boolean;
	readonly transfer_ownership: boolean;
}

export interface ConfigurationStore {
	readonly id: string;
	readonly name: string;
	readonly slug: string;
	readonly timezone: string;
	readonly seating_duration_min: number;
	readonly booking_slot_interval_min: number;
	readonly min_booking_notice_min: number;
	readonly max_booking_days_ahead: number;
	readonly confirmation_required_above: number | null;
	readonly is_active: boolean;
}

export interface ConfigurationArea {
	readonly id: string;
	readonly store_id: string;
	readonly name: string;
	readonly is_active: boolean;
	readonly is_customer_selectable: boolean;
	readonly auto_assign_priority: number;
	readonly sort_order: number;
}

export interface ConfigurationTable {
	readonly id: string;
	readonly store_id: string;
	readonly area_id: string;
	readonly name: string;
	readonly seats: number;
	readonly is_active: boolean;
}

export interface ScheduleWindow {
	readonly id?: string;
	readonly weekday?: number;
	readonly open_time: string;
	readonly close_time: string;
	readonly sort_order: number;
}

export interface ScheduleException {
	readonly id: string;
	readonly service_date: string;
	readonly is_closed: boolean;
	readonly reason: string | null;
	readonly store_schedule_exception_windows: readonly ScheduleWindow[];
}

export interface OnlineBookingWindow {
	readonly id?: string;
	readonly weekday?: number;
	readonly first_start_time: string;
	readonly last_start_time: string;
	readonly sort_order: number;
}

export interface OnlineBookingException {
	readonly id: string;
	readonly service_date: string;
	readonly is_disabled: boolean;
	readonly reason: string | null;
	readonly online_booking_schedule_exception_windows: readonly OnlineBookingWindow[];
}

export interface ConfigurationUser {
	readonly id: string;
	readonly full_name: string;
	readonly username: string | null;
	readonly contact_email: string | null;
	readonly role: ConfigurationRole;
	readonly default_store_id: string | null;
	readonly is_active: boolean;
	readonly user_stores: readonly { readonly store_id: string }[];
}

export interface StoreDomain {
	readonly id: string;
	readonly store_id: string;
	readonly domain: string;
	readonly is_primary: boolean;
	readonly verified_at: string | null;
	readonly ssl_status: 'pending' | 'active' | 'failed';
	readonly provider_domain_id: string | null;
	readonly provisioning_error: string | null;
	readonly provisioning_checked_at: string | null;
	readonly challenge: {
		readonly record_name: string;
		readonly expected_value: string;
		readonly expires_at: string;
	} | null;
}

export interface ConfigurationContext {
	readonly user: {
		readonly id: string;
		readonly full_name: string;
		readonly role: ConfigurationRole;
	};
	readonly capabilities: ConfigurationCapabilities;
	readonly selected_store_id: string | null;
	readonly stores: readonly ConfigurationStore[];
	readonly areas: readonly ConfigurationArea[];
	readonly tables: readonly ConfigurationTable[];
	readonly hours: readonly ScheduleWindow[];
	readonly exceptions: readonly ScheduleException[];
	readonly online_booking_hours: readonly OnlineBookingWindow[];
	readonly online_booking_exceptions: readonly OnlineBookingException[];
	readonly users: readonly ConfigurationUser[];
	readonly domains: readonly StoreDomain[];
}
